import { useState, useRef, useEffect } from 'react'
import Button from '../Button'
import iconChevronDown from '../../assets/icons/chevron-down.svg'
import iconSearch from '../../assets/icons/search.svg'
import './styles.css'

// Icono de check como SVG inline
const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.33334 2.5L3.75001 7.08333L1.66667 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

function Dropdown({ options = [], value, onChange, className = '', placeholder = '', searchable = true, multiple = false }) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const dropdownRef = useRef(null)
  const searchInputRef = useRef(null)

  // Normalizar value como array si es multiple
  const selectedValues = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // Focus en el input de búsqueda cuando se abre
      if (searchable && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 50)
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, searchable])

  const selectedOption = options.find(opt => opt.value === value)

  const handleOptionClick = (optionValue) => {
    if (multiple) {
      // Modo múltiple: toggle de la opción
      const currentValues = Array.isArray(value) ? value : []
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue]
      onChange(newValues)
      // No cerrar el dropdown en modo múltiple
    } else {
      // Modo simple: seleccionar y cerrar
      onChange(optionValue)
      setIsOpen(false)
      setSearchTerm('')
    }
  }

  // Obtener el texto del botón
  const getButtonLabel = () => {
    if (multiple) {
      const selectedCount = selectedValues.length
      if (selectedCount === 0) return placeholder || 'Seleccionar'
      if (selectedCount === 1) {
        const selected = options.find(opt => opt.value === selectedValues[0])
        return selected?.label || placeholder || 'Seleccionar'
      }
      return `${selectedCount} seleccionados`
    }
    return selectedOption?.label || placeholder || 'Seleccionar'
  }

  // Verificar si una opción está seleccionada
  const isSelected = (optionValue) => {
    return selectedValues.includes(optionValue)
  }

  // Filtrar opciones según el término de búsqueda
  const filteredOptions = searchable && searchTerm
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options

  return (
    <div className={`dropdown ${className}`} ref={dropdownRef}>
      <Button
        variant="primary"
        size="s"
        onClick={() => setIsOpen(!isOpen)}
        className="dropdown-trigger"
      >
        <span>{getButtonLabel()}</span>
        <img
          src={iconChevronDown}
          alt=""
          className={`dropdown-chevron ${isOpen ? 'dropdown-chevron--open' : ''}`}
        />
      </Button>
      {isOpen && (
        <div className="dropdown-menu">
          {searchable && options.length > 5 && (
            <div className="dropdown-search">
              <img src={iconSearch} alt="" className="dropdown-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="dropdown-search-input"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className="dropdown-options">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`dropdown-item ${isSelected(option.value) ? 'dropdown-item--selected' : ''}`}
                  onClick={() => handleOptionClick(option.value)}
                >
                  <div className="dropdown-checkbox">
                    {isSelected(option.value) && <CheckIcon />}
                  </div>
                  <span className="dropdown-item-text">{option.label}</span>
                </div>
              ))
            ) : (
              <div className="dropdown-no-results">
                No se encontraron resultados
              </div>
            )}
          </div>
          {multiple && selectedValues.length > 0 && (
            <div className="dropdown-footer">
              <button
                className="dropdown-clear-button"
                onClick={(e) => {
                  e.stopPropagation()
                  onChange([])
                }}
              >
                Limpiar selección
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Dropdown
